import React, { useState, useEffect, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup, { BUTTON_LOADING_ICON, FORM_COMPACT_DANGER_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import StatusChip, { ORDER_CLAIM_STATUS_CHIP } from '../../component/StatusChip'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatLocaleDateTime } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'

const MAX_VISIBLE_ACTION_BLOCK_REASONS = 4

const getVisibleActionBlockReasons = (reasons = []) => reasons.slice(0, MAX_VISIBLE_ACTION_BLOCK_REASONS)
const getProductOptionName = (detail) => detail.productOptionName || detail.product_option?.name || ''

export default function Payment(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.orderDetailId
    const searchText = locationState.searchText
    const searchSelect = locationState.searchSelect
    const startTime = locationState.startTime
    const endTime = locationState.endTime
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [data, setData] = useState({})
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar || '')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const claims = Array.isArray(data?.claims) ? data.claims : []
    const claimSummary = data?.claimSummary || {}
    const availableActions = data?.availableActions || {}
    const statusLabels = data?.statusLabels || {}
    const hasClaim = claims.length > 0
    const canCancel = Boolean(availableActions?.canCardCancel)
    const deviceTypeMap = { 1: 'web', 2: 'and', 3: 'ios' }

    const paymentStatusChip = (payStatus, payMethod) => {
        if (payStatus === 'paid') return <StatusChip label="결제완료" color="success" />
        if (payStatus === 'ready' && payMethod === 'vbank') return <StatusChip label="입금대기" color="info" />
        if (payStatus === 'cancelled') return <StatusChip label="취소완료" color="warning" />
        return <StatusChip label={payStatus || '미결제'} color="default" />
    }

    const formatCurrency = (value) => `${formatNumber(value)}원`

    // 결제 상세에서 나가도 날짜/검색 필터가 유지되도록 목록 컨텍스트를 묶어서 보낸다.
    const moveToList = useCallback(() => {
        history.push(createListLinkTarget({
            pathname: '/payments',
            search: listSearch,
            searchText,
            searchSelect,
            curPage: locationState.curPage,
            startTime,
            endTime,
        }))
    }, [endTime, history, listSearch, locationState.curPage, searchSelect, searchText, startTime])

    const resetDetailState = useCallback((errorMessage) => {
        setData({})
        setHasDetail(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 결제 상세를 불러올 수 없습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/payment/getPayment', { orderDetailId: id })
            if (data.code === 1) {
                const detail = data.data || {}

                if (!detail.orderDetailId) {
                    resetDetailState('결제 상세 정보를 찾을 수 없습니다.')
                    return
                }

                setData(detail)
                setHasDetail(true)
                setDetailErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '결제 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (error) {
            console.error('getPayment error:', error)
            const errorMessage = '결제 상세를 불러오지 못했습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history, id, resetDetailState])

    useEffect(() => {
        getData()
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
    }

    // 결제 상세에서 클레임 상세를 열면, 처리 후에도 다시 현재 결제 상세로 복귀할 수 있게 출발점을 같이 넘긴다.
    const goToClaimDetail = (orderClaimId) => {
        if (!orderClaimId || saving) return
        history.push(createListLinkTarget({
            pathname: `/orderClaim/${orderClaimId}`,
            returnTarget: createListLinkTarget({
                pathname: `/payment/${id}`,
                search: listSearch,
                searchText,
                searchSelect,
                curPage: locationState.curPage,
                startTime,
                endTime,
            }),
        }))
    }

    const goToClaimList = () => {
        if (saving) return
        history.push({ pathname: '/orderClaims' })
    }

    const InfoCard = ({ title, children }) => (
        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle1" gutterBottom>{title}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Grid container spacing={1.5}>
                {children}
            </Grid>
        </Paper>
    )

    const Field = ({ label, value }) => {
        const displayValue = value === null || value === undefined || value === '' ? '-' : value

        return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}><strong>{displayValue}</strong></Typography>
            </Grid>
        )
    }

    const shippingStatusText = statusLabels?.shippingStatusName || (() => {
        const status = Number(data?.shippingStatus)
        if (Number.isNaN(status)) return '-'
        if (status === 0) return '미정'
        if (status === 1) return '상품준비중'
        if (status === 2) return '배송중'
        if (status === 3) return '배송완료'
        return `${status}`
    })()

    const shippingStatusChip = (shippingStatus) => {
        const status = Number(shippingStatus)
        if (status === 1) return <StatusChip label="상품준비중" color="warning" />
        if (status === 2) return <StatusChip label="배송중" color="primary" />
        if (status === 3) return <StatusChip label="배송완료" color="success" />
        return <StatusChip label={shippingStatusText} />
    }

    const claimChip = (() => {
        if (!hasClaim) return <StatusChip color="success" label="클레임 없음" />
        if (claimSummary?.pendingCount > 0) return <StatusChip color="warning" label={`진행중 ${claimSummary.pendingCount}건`} />
        if (claimSummary?.rejectedCount > 0 && claimSummary?.completedCount === 0) return <StatusChip color="error" label={`반려 ${claimSummary.rejectedCount}건`} />
        return <StatusChip color="info" label={`클레임 ${claimSummary?.totalCount || claims.length}건`} />
    })()

    const claimTypeChip = (claimTypeName) => {
        if (claimTypeName === '취소') return <StatusChip label={claimTypeName} color="default" />
        if (claimTypeName === '환불') return <StatusChip label={claimTypeName} color="info" />
        if (claimTypeName === '반품') return <StatusChip label={claimTypeName} color="secondary" />
        return <StatusChip label={claimTypeName || '-'} />
    }

    const claimStatusChip = (status, statusName) => {
        return <StatusChip status={status} config={ORDER_CLAIM_STATUS_CHIP} label={statusName} />
    }

    const actionAlert = (() => {
        if (availableActions?.hasPendingClaim) {
            return {
                severity: 'warning',
                message: availableActions?.summaryMessage || '진행 중인 클레임이 있어 기존 신청 건 확인이 우선입니다.'
            }
        }

        if (availableActions?.canCardCancel) {
            return {
                severity: 'success',
                message: availableActions?.summaryMessage || '카드 결제 취소 가능 상태입니다.'
            }
        }

        if (availableActions?.canCreateRefundClaim || availableActions?.canCreateReturnClaim) {
            return {
                severity: 'info',
                message: availableActions?.summaryMessage || '클레임 신청 가능 상태입니다.'
            }
        }

        if (hasClaim) {
            return {
                severity: 'info',
                message: availableActions?.summaryMessage || '기존 클레임 이력을 확인하세요.'
            }
        }

        return {
            severity: 'info',
            message: availableActions?.summaryMessage || '현재 결제 상세에서 즉시 처리 가능한 액션이 없습니다.'
        }
    })()

    const actionGuideText = (() => {
        if (availableActions?.hasPendingClaim) {
            return '이미 접수된 클레임이 있습니다. 관리자 직접 신청이 아니라 클레임 상세에서 승인 또는 반려를 진행하세요.'
        }
        if (canCancel) {
            return '카드 결제 건이며 클레임 이력이 없어서 이 화면에서 즉시 취소할 수 있습니다.'
        }
        if (availableActions?.canCreateRefundClaim) {
            return '고객이 환불 신청할 수 있는 상태입니다. 어드민은 신청을 대신하지 않고, 신청 후 클레임 상세에서 처리합니다.'
        }
        if (availableActions?.canCreateReturnClaim) {
            return '고객이 반품 신청할 수 있는 상태입니다. 어드민은 신청을 대신하지 않고, 신청 후 클레임 상세에서 처리합니다.'
        }
        if (hasClaim) {
            return '신청 이력은 남아 있으나 현재 이 화면에서 직접 실행할 관리 액션은 없습니다. 최근 클레임 상세를 확인하세요.'
        }
        return '현재 상태에서는 고객 신청 대기 또는 배송 상태 변경 이후에만 후속 처리가 가능합니다.'
    })()

    const actionBlockReasons = Array.from(new Set([
        ...(
            canCancel || availableActions?.canCreateRefundClaim || availableActions?.canCreateReturnClaim
                ? []
                : data.order?.payment?.payMethod === 'card'
                    ? (availableActions?.cardCancelBlockReasons || [])
                    : Number(data.shippingStatus) >= 2
                        ? (availableActions?.returnClaimBlockReasons || [])
                        : (availableActions?.refundClaimBlockReasons || [])
        )
    ]))

    // 카드결제취소
    const cardCancel = async (orderId) => {
        if (saving) return
        if (!window.confirm("결제를 취소하시겠습니까?")){
            return
        }
        if (!canCancel || !orderId) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('카드 취소 가능한 상태가 아닙니다.')
            return
        }

        setSaving(true)

        try {
            const response = await API.post('/api/v1/admin/payment/cancel', {
                userNo: data?.order?.user?.userNo,
                userId: data?.order?.userId,
                claimItems: [{
                    orderDetailId: id,
                    claimQuantity: data?.quantity || 0,
                }],
                claimReasonType: 3,
                claimReason: '관리자 결제취소'
            })
            if (response.data.code === 200) {
                await getData()
                setOpenSnackbar(true)
                setSeveritySnackbar('info')
                setMessageSnackbar(response.data.message)
            } else if (response.data.code === 400 && response.data.message?.includes('상품준비중인')) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('결제 실패. 상품이 배송중입니다.')
                await getData()
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.data.message)
            }
        } catch (error) {
            console.error('cancelPayment error:', error)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('카드 결제 취소 중 오류가 발생했습니다.')
        } finally {
            setSaving(false)
        }
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="결제 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            {loading ? (
                    <Spiner/>
                ) : !hasDetail ? (
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Title>결제 상세</Title>
                        {renderDetailErrorState()}
                    </Paper>
                ) : (
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>결제 상세</Title>
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <InfoCard title="결제 요약">
                                        <Grid item xs={12} md={12} lg={12}>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                {paymentStatusChip(data.payStatus, data.order?.payment?.payMethod)}
                                                {shippingStatusChip(data.shippingStatus)}
                                                {claimChip}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                주문건: {data.order?.orderId || '-'} / 상세주문: {data.orderDetailId || '-'} / 현재 액션: {availableActions?.primaryActionLabel || '-'}
                                            </Typography>
                                        </Grid>
                                        <Field label="결제일" value={formatLocaleDateTime(data.order?.payment?.pgTime)} />
                                        <Field label="취소일" value={formatLocaleDateTime(data.cancelTime)} />
                                        <Field label="결제수단" value={statusLabels?.payMethodName || data.order?.payment?.payMethod || '-'} />
                                        <Field label="주문항목 결제금액" value={formatCurrency(data.payAmount)} />
                                        <Field label="주문 전체 결제금액" value={formatCurrency(data.order?.totalPayPrice)} />
                                        <Field label="할부" value={data.order?.payment?.cardQuota || '-'} />
                                        <Field label="기기" value={deviceTypeMap[data.order?.payment?.deviceType] || data.order?.payment?.deviceType || '-'} />
                                        <Field label="승인번호" value={data.order?.payment?.applyNum || '-'} />
                                        <Field label="아임포트ID" value={data.order?.payment?.impId || '-'} />
                                        <Field label="PG 주문번호" value={data.order?.payment?.pgId || '-'} />
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InfoCard title="상품 정보">
                                        <Field label="상품명" value={`${data.product_category?.product?.title || '-'} ${data.quantity || 0}개`} />
                                        <Field label="카테고리" value={data.product_category?.name || '-'} />
                                        {getProductOptionName(data) && <Field label="필수옵션" value={getProductOptionName(data)} />}
                                        <Field label="수량" value={`${data.quantity || 0}개`} />
                                        <Field label="주문항목 결제금액" value={formatCurrency(data.payAmount)} />
                                        <Field label="취소금액" value={formatCurrency(data.cancelAmount)} />
                                        <Field label="사용 쿠폰" value={formatCurrency(data.couponDiscountPrice)} />
                                        <Field label="사용 포인트" value={`${formatNumber(data.rewardDiscountPrice)}P`} />
                                        <Field label="추가 클레임 가능 수량" value={`${data.claimAvailability?.remainingClaimQuantity ?? 0}개`} />
                                        <Grid item xs={12} sm={6} md={12} lg={12}>
                                            <Typography variant="body2" color="text.secondary">클레임 현황</Typography>
                                            <Box sx={{ mt: 0.5 }}>{claimChip}</Box>
                                        </Grid>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InfoCard title="구매자/배송 정보">
                                        <Field label="구매자명" value={data.order?.user?.username || '-'} />
                                        <Field label="구매자 연락처" value={data.order?.user?.phone || '-'} />
                                        <Field label="수취인명" value={data.order?.payment?.buyerName || '-'} />
                                        <Field label="수취인 연락처" value={data.order?.payment?.buyerPhone || '-'} />
                                        <Field label="우편번호" value={data.order?.payment?.buyerZipcode || '-'} />
                                        <Grid item xs={12} sm={12} md={12} lg={12}>
                                            <Typography variant="body2" color="text.secondary">배송주소</Typography>
                                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                                                {`${data.order?.payment?.buyerAddress || '-'} ${data.order?.payment?.buyerAddressDetail || ''}`}
                                            </Typography>
                                        </Grid>
                                        <Field label="배송요청사항" value={data.order?.shippingMemo || '-'} />
                                        <Grid item xs={12} sm={12} md={12} lg={12}>
                                            <Typography variant="body2" color="text.secondary">배송상태</Typography>
                                            <Box sx={{ mt: 0.5 }}>{shippingStatusChip(data.shippingStatus)}</Box>
                                        </Grid>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fff8e1' }}>
                                        <Typography variant="subtitle1" gutterBottom>클레임 요약</Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <Alert severity={hasClaim ? (claimSummary?.pendingCount > 0 ? 'warning' : claimSummary?.rejectedCount > 0 ? 'error' : 'info') : 'success'} sx={{ mb: 2 }}>
                                            {claimSummary?.statusMessage || '클레임 없음'}
                                        </Alert>
                                        <Grid container spacing={1.5}>
                                            <Field label="전체 클레임" value={`${claimSummary?.totalCount || 0}건`} />
                                            <Field label="진행중" value={`${claimSummary?.pendingCount || 0}건`} />
                                            <Field label="완료" value={`${claimSummary?.completedCount || 0}건`} />
                                            <Field label="반려" value={`${claimSummary?.rejectedCount || 0}건`} />
                                            <Field label="최근 클레임" value={claimSummary?.latestClaim ? `${claimSummary.latestClaim.claimTypeName} / ${claimSummary.latestClaim.statusName}` : '-'} />
                                            <Field label="최근 사유" value={claimSummary?.latestClaim?.claimReason || '-'} />
                                        </Grid>
                                        {claimSummary?.latestClaim?.orderClaimId && (
                                            <Box sx={{ mt: 2 }}>
                                                <Button variant="outlined" size="small" onClick={() => goToClaimDetail(claimSummary.latestClaim.orderClaimId)} disabled={saving}>
                                                    최근 클레임 상세 보기
                                                </Button>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#fcfcfc' }}>
                                        <Typography variant="subtitle1" gutterBottom>클레임 이력</Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        {hasClaim ? (
                                            <EmbeddedTableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>클레임번호</TableCell>
                                                            <TableCell>타입</TableCell>
                                                            <TableCell>상태</TableCell>
                                                            <TableCell align="right">수량</TableCell>
                                                            <TableCell align="right">클레임금액</TableCell>
                                                            <TableCell align="right">배송비차감</TableCell>
                                                            <TableCell align="right">예상환불액</TableCell>
                                                            <TableCell>사유</TableCell>
                                                            <TableCell>신청일</TableCell>
                                                            <TableCell>처리일</TableCell>
                                                            <TableCell align="right">Action</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {claims.map((claim) => (
                                                            <TableRow key={claim.orderClaimDetailId}>
                                                                <TableCell>{claim.orderClaimNo}</TableCell>
                                                                <TableCell>{claimTypeChip(claim.claimTypeName)}</TableCell>
                                                                <TableCell>{claimStatusChip(claim.status, claim.statusName)}</TableCell>
                                                                <TableCell align="right">{claim.claimQuantity}</TableCell>
                                                                <TableCell align="right">{formatCurrency(claim.claimAmount)}</TableCell>
                                                                <TableCell align="right">-{formatCurrency(claim.shippingAmount)}</TableCell>
                                                                <TableCell align="right">{formatCurrency(claim.actualRefundAmount)}</TableCell>
                                                                <TableCell>{claim.claimReason}</TableCell>
                                                                <TableCell>{formatLocaleDateTime(claim.regTime)}</TableCell>
                                                                <TableCell>{formatLocaleDateTime(claim.processTime)}</TableCell>
                                                                <TableCell align="right">
                                                                    <Button size="small" variant="text" onClick={() => goToClaimDetail(claim.orderClaimId)} disabled={saving}>
                                                                        상세
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </EmbeddedTableContainer>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                등록된 클레임 이력이 없습니다.
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f4f8ff' }}>
                                        <Typography variant="subtitle1" gutterBottom>관리 액션</Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <Alert severity={actionAlert.severity} sx={{ mb: 2 }}>
                                            {actionAlert.message}
                                        </Alert>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {actionGuideText}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: actionBlockReasons.length > 0 ? 2 : 0 }}>
                                            {data.order?.payment?.payMethod === 'card' && canCancel && (
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    sx={FORM_COMPACT_DANGER_ACTION_BUTTON_SX}
                                                    onClick={() => cardCancel(data.order?.orderId)}
                                                    disabled={saving}
                                                    startIcon={saving ? BUTTON_LOADING_ICON : undefined}
                                                    aria-busy={saving}
                                                >
                                                    {saving ? '취소 중...' : '카드결제취소'}
                                                </Button>
                                            )}
                                            {availableActions?.canCreateRefundClaim && <StatusChip color="info" label="고객 환불 신청 가능" />}
                                            {availableActions?.canCreateReturnClaim && <StatusChip color="secondary" label="고객 반품 신청 가능" />}
                                            {hasClaim && (
                                                <Button variant="outlined" size="small" onClick={goToClaimList} disabled={saving}>
                                                    클레임 목록
                                                </Button>
                                            )}
                                            {claimSummary?.latestClaim?.orderClaimId && (
                                                <Button variant="text" size="small" onClick={() => goToClaimDetail(claimSummary.latestClaim.orderClaimId)} disabled={saving}>
                                                    최근 클레임 상세
                                                </Button>
                                            )}
                                        </Box>
                                        {!canCancel && actionBlockReasons.length > 0 && (
                                            <Box>
                                                {getVisibleActionBlockReasons(actionBlockReasons).map((reason) => (
                                                    <Typography key={reason} variant="body2" color="text.secondary">
                                                        - {reason}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                            <BottomButtonGroup onBackClick={moveToList} backLabel="뒤로" backDisabled={saving} />
                        </Box>
                    </Paper>
                )}
            {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
        </Grid>
    )
}
